import { TableItem, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { ResponsesPositionItemResponse } from '@genial/services/dist/models';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box, Spacer } from 'native-base';
import React from 'react';

import { fundPositionStatememntsWidthCols } from '../../../../../../constants';
import { validateAndFormatValue } from '../../../../Table/constants';

interface PositionItemDetailsProps {
  fundPositions: ResponsesPositionItemResponse[];
}

const PositionItem = ({ label, value }: { label: string; value: string }) => (
  <HStack alignItems={'center'} justifyContent={'space-between'}>
    <Text fontSize={Typography['fontsize.md']} variant={'regular'} color={'neutral.dark'}>
      {label}
    </Text>
    <Text fontSize={Typography['fontsize.md']} variant={'regular'} color={'neutral.dark'}>
      {value}
    </Text>
  </HStack>
);

export const PositionItemDetails: React.FC<PositionItemDetailsProps> = ({ fundPositions }) => {
  const positionDesktopItemDetailComponent = fundPositions?.map((position, index) => (
    <HStack
      key={`${position.netValue}-${index}`}
      bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
      w={'full'}
      borderRadius={BoxRadius.sm}
      py={Spacing['inline.nano']}>
      <TableItem
        testId="statement-fund-position-item-name"
        width={fundPositionStatememntsWidthCols.name.desktop}>
        <Spacer />
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-application-date"
        width={fundPositionStatememntsWidthCols.applicationDate.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.applicationDate, 'date')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-profitability"
        width={fundPositionStatememntsWidthCols.profitability.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.profitability, 'percentage')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-quota-value"
        width={fundPositionStatememntsWidthCols.quota.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.quotaValue, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-quota-quantity"
        width={fundPositionStatememntsWidthCols.quantity.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.quotaQuantity, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-value-applied"
        width={fundPositionStatememntsWidthCols.valueApplied.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.applicationValue, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-value-gross-value"
        width={fundPositionStatememntsWidthCols.grossValue.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.grossValue, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-ir-value"
        width={fundPositionStatememntsWidthCols.ir.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.irValue, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-iof-value"
        width={fundPositionStatememntsWidthCols.iof.desktop}>
        <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
          {validateAndFormatValue(position?.iofValue, 'money')}
        </Text>
      </TableItem>
      <TableItem
        testId="statement-fund-position-item-total-net-value"
        width={fundPositionStatememntsWidthCols.netValue.desktop}>
        <HStack alignItems={'center'} w={'full'} justifyContent={'space-between'}>
          <Text fontSize={Typography['fontsize.xs']} noOfLines={1} variant={'regular'}>
            {validateAndFormatValue(position?.netValue, 'money')}
          </Text>
        </HStack>
      </TableItem>
    </HStack>
  ));

  const positionTableItemDetailComponent = fundPositions?.map((position, index, array) => (
    <VStack key={`${position.netValue}-${index}`} space={Spacing['inset.nano']}>
      <PositionItem
        label="Aplicação"
        value={validateAndFormatValue(position?.applicationDate, 'date')}
      />
      <PositionItem
        label="Rent."
        value={validateAndFormatValue(position?.profitability, 'percentage')}
      />
      <PositionItem label="Cota" value={validateAndFormatValue(position?.quotaValue, 'money')} />
      <PositionItem
        label="Quantidade"
        value={validateAndFormatValue(position?.quotaQuantity, 'money')}
      />
      <PositionItem
        label="Valor aplicado"
        value={validateAndFormatValue(position?.applicationValue, 'money')}
      />
      <PositionItem
        label="Valor Bruto"
        value={validateAndFormatValue(position?.grossValue, 'money')}
      />
      <PositionItem label="IR" value={validateAndFormatValue(position?.irValue, 'money')} />
      <PositionItem label="IOF" value={validateAndFormatValue(position?.iofValue, 'money')} />
      <PositionItem
        label="Valor líquido"
        value={validateAndFormatValue(position?.netValue, 'money')}
      />
      {array.length - 1 !== index && <Box h={0.6} w={'full'} bg={'neutral.lightest'} />}
    </VStack>
  ));

  const positionItemDetailsResponsive = useBreakpointValue({
    base: (
      <VStack
        bg={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
        w={'full'}
        space={Spacing['inline.xxxs']}
        borderRadius={BoxRadius.sm}
        py={Spacing['inline.nano']}
        px={Spacing['inline.xxxs']}>
        {positionTableItemDetailComponent}
      </VStack>
    ),
    lg: positionDesktopItemDetailComponent,
  });

  return positionItemDetailsResponsive;
};
